import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMqttMeterReadings } from "@/api/reseller/queries";
import type { MqttMeterReading } from "@/api/reseller/types";
import {
	type Column,
	MeterTable,
	usePagination,
} from "@/components/meters/meter-table";
import { ReadingStatusBadge } from "@/components/mqtt/reading-status-badge";
import { CopyableCell } from "@/components/shared/copyable-cell";
import { Badge } from "@/components/ui/badge";

type Props = {
	districtId: number;
	readingId: number;
};

export function MqttMeterReadingsTable({ districtId, readingId }: Props) {
	const { t, i18n } = useTranslation();
	const { page, setPage } = usePagination();
	const [pageSize, setPageSize] = useState(20);
	const [search, setSearch] = useState("");
	const [ordering, setOrdering] = useState("");

	const { data, isLoading } = useMqttMeterReadings(districtId, readingId, {
		page,
		page_size: pageSize,
		search: search || undefined,
		ordering: ordering || undefined,
	});

	const columns: Column<MqttMeterReading>[] = [
		{
			key: "meter",
			label: t("mqttMeterReadings.colSerialNumber"),
			sortField: "meter__serial_number",
			render: (row) => (
				<CopyableCell
					value={row.meter.serial_number}
					label={t("mqttMeterReadings.colSerialNumber")}
				/>
			),
		},
		{
			key: "status",
			label: t("mqttMeterReadings.colStatus"),
			render: (row) => <ReadingStatusBadge status={row.status} />,
		},
		{
			key: "previous_status",
			label: t("mqttMeterReadings.colPrevStatus"),
			render: (row) =>
				row.previous_status ? (
					<Badge variant="secondary">{row.previous_status}</Badge>
				) : (
					<span className="text-muted-foreground">—</span>
				),
		},
		{
			key: "last_communication",
			label: t("mqttMeterReadings.colLastCommunication"),
			render: (row) =>
				row.last_communication
					? new Date(row.last_communication).toLocaleString(i18n.language, {
							dateStyle: "short",
							timeStyle: "short",
						})
					: "—",
		},
	];

	return (
		<MeterTable
			data={data}
			columns={columns}
			loading={isLoading}
			page={page}
			pageSize={pageSize}
			onPageChange={setPage}
			onPageSizeChange={(size) => {
				setPageSize(size);
				setPage(1);
			}}
			search={search}
			onSearchChange={(val) => {
				setSearch(val);
				setPage(1);
			}}
			ordering={ordering}
			onOrderingChange={(val) => {
				setOrdering(val);
				setPage(1);
			}}
		/>
	);
}
